import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountService } from '@services/accountService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'accounts';

/**
 * Hook para gerenciar contas
 */
export const useAccounts = () => {
  const queryClient = useQueryClient();

  // Listar contas
  const {
    data: accounts = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: accountService.getAll,
  });

  // Criar conta
  const createMutation = useMutation({
    mutationFn: accountService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Conta criada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar conta');
    },
  });

  // Atualizar conta
  const updateMutation = useMutation({
    mutationFn: ({ accountId, data }) => accountService.update(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Conta atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar conta');
    },
  });

  // Deletar conta
  const deleteMutation = useMutation({
    mutationFn: accountService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Conta excluída com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir conta');
    },
  });

  // Transferência
  const transferMutation = useMutation({
    mutationFn: accountService.transfer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Transferência realizada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao realizar transferência');
    },
  });

  // Ordenar por nome
  const sortedAccounts = [...accounts].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'pt-BR')
  );

  // Saldo em conta (cash) vs dívida de cartão — espelha o backend (getDashboardData).
  // totalBalance = patrimônio líquido (soma tudo, incl. cartão como dívida).
  const cashBalance = accounts.reduce(
    (sum, acc) => sum + (acc.type !== 'credit_card' ? (acc.currentBalance || 0) : 0),
    0
  );
  const creditCardDebt = accounts.reduce(
    (sum, acc) => sum + (acc.type === 'credit_card' ? (acc.currentBalance || 0) : 0),
    0
  );
  const totalBalance = cashBalance + creditCardDebt;

  return {
    accounts: sortedAccounts,
    totalBalance,
    cashBalance,
    creditCardDebt,
    isLoading,
    error,
    refetch,
    createAccount: createMutation.mutateAsync,
    updateAccount: updateMutation.mutateAsync,
    deleteAccount: deleteMutation.mutateAsync,
    transfer: transferMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isTransferring: transferMutation.isPending,
  };
};

export default useAccounts;
